import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  LoaderCircle,
  Brain,
  Split,
  Check,
  Trash2,
  Wand2,
  RotateCw,
  History,
} from "lucide-react";
import {
  contentQueries,
  useEditContentWithFeedbackMutation,
  useSaveEditedContentMutation,
} from "@/api/longform";
import { useQuery } from "@tanstack/react-query";

interface EditWithFeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contentId: string;
  personaId: string;
}

const LOADING_MESSAGES = [
  "Analyzing your feedback...",
  "Understanding content context...",
  "Applying requested changes...",
  "Maintaining the agent's voice and style...",
  "Refining grammar and structure...",
  "Finalizing the edited content...",
];

const EditWithFeedbackDialog = ({
  open,
  onOpenChange,
  contentId,
  personaId,
}: EditWithFeedbackDialogProps) => {
  const { toast } = useToast();

  // State management
  const [feedback, setFeedback] = useState("");
  const [editHistory, setEditHistory] = useState<
    {
      original: { title: string; content: string };
      edited: { title: string; content: string };
      feedback: string;
    }[]
  >([]);
  const [currentEditIndex, setCurrentEditIndex] = useState(0);
  const [fullContent, setFullContent] = useState<{
    title: string;
    content: string;
  } | null>(null);
  const [viewMode, setViewMode] = useState<"input" | "preview">("input");
  const saveEditedContentMutation = useSaveEditedContentMutation();
  const editContentWithFeedbackMutation = useEditContentWithFeedbackMutation();
  const isSubmitting = editContentWithFeedbackMutation.isPending;
  const isSaving = saveEditedContentMutation.isPending;

  const { data: contentData, isLoading: isContentLoading } = useQuery({
    ...contentQueries.contentList(personaId),
    select: (data) =>
      data.data.find((content) => content.content_id === contentId),
  });

  // Current loading message being displayed
  const [loadingMessageIndex, setLoadingMessageIndex] = useState<number | null>(
    null,
  );
  const isProcessing = loadingMessageIndex !== null;

  // Get the current edit from history
  const currentEdit = editHistory[currentEditIndex] || null;

  // Fetch the full content when the dialog opens
  useEffect(() => {
    if (open && contentId && contentData) {
      setFullContent({
        title: contentData.title,
        content: contentData.content,
      });
      // Reset the state when the dialog opens
      setFeedback("");
      setEditHistory([]);
      setCurrentEditIndex(0);
      setViewMode("input");
    }
  }, [open, contentId, contentData]);

  const handleSubmitFeedback = async () => {
    if (!feedback.trim()) {
      toast({ description: "Please provide feedback for the edit." });
      return;
    }

    setLoadingMessageIndex(0);
    const interval = setInterval(() => {
      setLoadingMessageIndex(
        (prev) => ((prev ?? -1) + 1) % LOADING_MESSAGES.length,
      );
    }, 2500);

    // Determine what content to use as the base for this edit
    const baseContent =
      editHistory.length > 0
        ? editHistory[currentEditIndex].edited
        : {
            title: fullContent?.title || "",
            content: fullContent?.content || "",
          };

    // Create a content data object to send for editing
    const contentData = {
      title: baseContent.title,
      content: baseContent.content,
      metadata: { persona_id: personaId },
    };

    editContentWithFeedbackMutation.mutate(
      {
        personaId,
        contentId,
        feedback,
        content: contentData,
      },
      {
        onSuccess: (result) => {
          // Create a new edit history entry
          const newEdit = {
            original: {
              title: baseContent.title,
              content: baseContent.content,
            },
            edited: {
              title: result.edited.title,
              content: result.edited.content,
            },
            feedback: feedback,
          };

          // Add this edit to the history and make it the current one
          setEditHistory((prev) => [...prev, newEdit]);
          setCurrentEditIndex(editHistory.length); // Set to the newly added edit
          setViewMode("preview");
          setFeedback(""); // Clear feedback for potential next iteration

          toast({ description: "Edit generated successfully!" });
        },
        onError: (error: any) => {
          console.error("Error generating edit:", error);
          toast({
            description:
              error.response?.data?.detail ||
              "Error generating edit. Please try again.",
            variant: "destructive",
          });
        },
        onSettled: () => {
          clearInterval(interval);
          setLoadingMessageIndex(null);
        },
      },
    );
  };

  // Save the edited content
  const handleSaveEdit = async () => {
    if (!currentEdit) return;

    const savePayload = {
      title: currentEdit.edited.title,
      content: currentEdit.edited.content,
      metadata: {
        edited: true,
        edit_feedback: currentEdit.feedback,
        edit_timestamp: new Date().toISOString(),
      },
    };

    saveEditedContentMutation.mutate(
      {
        personaId,
        contentId,
        saveOptions: savePayload,
      },
      {
        onSuccess: () => {
          toast({ description: "Content saved successfully!" });
          onOpenChange(false);
        },
        onError: (error: any) => {
          console.error("Error saving edit:", error);
          toast({
            description:
              error.response?.data?.detail ||
              "Error saving edited content. Please try again.",
            variant: "destructive",
          });
        },
      },
    );
  };

  // Handle starting a new edit iteration
  const handleNewEdit = () => {
    setViewMode("input");
  };

  // Navigate through the edit history
  const goToPreviousEdit = () => {
    if (currentEditIndex > 0) {
      setCurrentEditIndex(currentEditIndex - 1);
    }
  };

  const goToNextEdit = () => {
    if (currentEditIndex < editHistory.length - 1) {
      setCurrentEditIndex(currentEditIndex + 1);
    }
  };

  // Reset the dialog state
  const handleCancel = () => {
    setFeedback("");
    setEditHistory([]);
    setCurrentEditIndex(0);
    setViewMode("input");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[85vw] max-h-[85vh] h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Brain className="h-5 w-5 mr-2" />
            AI-Assisted Content Editing
            {editHistory.length > 0 && (
              <span className="ml-4 text-sm font-normal text-muted-foreground">
                Iteration {currentEditIndex + 1} of {editHistory.length}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Main content with scrollable area */}
        <div className="flex-1 overflow-y-auto space-y-4 my-4 pr-2">
          {isContentLoading ? (
            <div className="flex justify-center items-center h-full">
              <LoaderCircle className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2">Loading content...</span>
            </div>
          ) : viewMode === "input" ? (
            // Feedback input form with content to edit
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Describe the changes you want to make to your content. Our AI
                will edit the content based on your feedback while maintaining
                your agent's unique voice and perspective.
              </p>
              <Textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Example: Make the tone more assertive, add more data to support the second point, expand the conclusion, etc."
                rows={5}
                className="w-full"
                disabled={isSubmitting}
              />

              {isProcessing && (
                <div className="rounded-lg border border-border bg-muted p-4 my-4">
                  <div className="flex items-center gap-3">
                    <LoaderCircle className="h-5 w-5 animate-spin text-primary" />
                    <p>{LOADING_MESSAGES[loadingMessageIndex]}</p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    This process may take a minute. The AI is carefully applying
                    your feedback while preserving your agent's distinctive
                    voice.
                  </p>
                </div>
              )}

              {/* Show the current content to edit */}
              <div className="rounded-lg border p-4 mt-6">
                <h3 className="text-xl font-semibold mb-3">
                  {editHistory.length > 0
                    ? editHistory[currentEditIndex].edited.title
                    : fullContent?.title}
                </h3>
                <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                  {editHistory.length > 0
                    ? editHistory[currentEditIndex].edited.content
                    : fullContent?.content}
                </div>
              </div>
            </div>
          ) : (
            // Edit preview with comparison
            <>
              {/* Edit history navigation if multiple edits exist */}
              {editHistory.length > 1 && (
                <div className="flex items-center justify-between mb-4 pb-2 border-b">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToPreviousEdit}
                    disabled={currentEditIndex === 0}
                  >
                    Previous Edit
                  </Button>
                  <div className="flex items-center">
                    <History className="h-4 w-4 mr-2 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Edit {currentEditIndex + 1} of {editHistory.length}
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToNextEdit}
                    disabled={currentEditIndex === editHistory.length - 1}
                  >
                    Next Edit
                  </Button>
                </div>
              )}

              {/* Display feedback used for this edit */}
              <div className="mb-4 bg-muted/30 p-3 rounded-md">
                <p className="text-sm font-medium mb-1">
                  Feedback for this edit:
                </p>
                <p className="text-sm italic">"{currentEdit?.feedback}"</p>
              </div>

              <Tabs defaultValue="comparison" className="w-full">
                <TabsList className="grid grid-cols-3 mb-4">
                  <TabsTrigger value="comparison">
                    <Split className="h-4 w-4 mr-2" /> Side by Side
                  </TabsTrigger>
                  <TabsTrigger value="original">Original</TabsTrigger>
                  <TabsTrigger value="edited">Edited</TabsTrigger>
                </TabsList>

                <TabsContent value="comparison" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-lg border p-4 overflow-y-auto">
                      <h4 className="font-semibold mb-2">Original</h4>
                      <h5 className="text-lg mb-2">
                        {currentEdit?.original.title}
                      </h5>
                      <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                        {currentEdit?.original.content}
                      </div>
                    </div>
                    <div className="rounded-lg border border-primary bg-primary/5 p-4 overflow-y-auto">
                      <h4 className="font-semibold mb-2">Edited</h4>
                      <h5 className="text-lg mb-2">
                        {currentEdit?.edited.title}
                      </h5>
                      <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                        {currentEdit?.edited.content}
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="original">
                  <div className="rounded-lg border p-4">
                    <h3 className="text-xl font-semibold mb-3">
                      {currentEdit?.original.title}
                    </h3>
                    <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                      {currentEdit?.original.content}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="edited">
                  <div className="rounded-lg border p-4">
                    <h3 className="text-xl font-semibold mb-3">
                      {currentEdit?.edited.title}
                    </h3>
                    <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                      {currentEdit?.edited.content}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>

        {/* Dialog actions */}
        <DialogFooter className="flex justify-between items-center mt-4 border-t pt-4">
          <div className="text-sm text-muted-foreground">
            {viewMode === "preview"
              ? "Review the changes and save if you're satisfied, or continue editing."
              : "Describe what changes you'd like to make to your content."}
          </div>

          <div className="flex gap-2">
            {viewMode === "input" ? (
              // Input mode buttons
              <>
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitFeedback}
                  disabled={isSubmitting || !feedback.trim()}
                >
                  <Wand2 className="h-4 w-4 mr-2" />
                  Generate Edit
                </Button>
              </>
            ) : (
              // Preview mode buttons
              <>
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Discard All
                </Button>
                <Button
                  variant="outline"
                  onClick={handleNewEdit}
                  disabled={isSaving}
                >
                  <RotateCw className="h-4 w-4 mr-2" />
                  Edit Again
                </Button>
                <Button onClick={handleSaveEdit} disabled={isSaving}>
                  {isSaving ? (
                    <LoaderCircle className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditWithFeedbackDialog;
